import React, { useEffect, useState } from 'react';
import { Loader2, Check, X, Star, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

const STATUSES = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const StarBar = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <Star key={n} className={`w-3.5 h-3.5 ${n <= value ? 'fill-[#F46A25] text-[#F46A25]' : 'text-neutral-300'}`} strokeWidth={1.5} />
    ))}
  </div>
);

const ReviewsAdmin = () => {
  const [status, setStatus] = useState('pending');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/reviews', { params: { status } })
      .then(r => setReviews(r.data || []))
      .catch(() => toast.error('Failed to load reviews'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const moderate = async (id, next) => {
    setBusyId(id);
    try {
      await api.put(`/admin/reviews/${id}/status`, { status: next });
      toast.success(`Review ${next}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success('Review deleted');
      load();
    } catch { toast.error('Delete failed'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-reviews">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-[#12263A]">Customer reviews</h2>
          <p className="text-xs text-neutral-600 mt-1">
            Approve reviews to show them on the product page + include them in the JSON-LD schema (star ratings in Google search results).
          </p>
        </div>
        <div className="flex items-center gap-1">
          {STATUSES.map(s => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              data-testid={`reviews-tab-${s.id}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-sm ${status === s.id ? 'bg-[#F46A25] text-white' : 'bg-neutral-100 text-[#12263A] hover:bg-neutral-200'}`}
            >{s.label}</button>
          ))}
          <button onClick={load} className="ml-2 text-xs border border-neutral-300 hover:border-[#F46A25] px-3 py-1.5 rounded-sm flex items-center gap-1" data-testid="reviews-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25] mx-auto" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-sm p-8 text-center" data-testid="reviews-empty">
          No reviews in this bucket.
        </div>
      ) : (
        <ul className="space-y-3">
          {reviews.map(r => (
            <li key={r.id} className="bg-white border border-neutral-200 rounded-sm p-4" data-testid={`admin-review-item-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StarBar value={r.rating} />
                    <span className="font-bold text-[#12263A] text-sm">{r.reviewer_name}</span>
                    <span className="text-[11px] text-neutral-500">· product: <code>{r.product_id}</code></span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span>
                  </div>
                  {r.title && <div className="font-semibold text-[#12263A] text-sm mb-1">{r.title}</div>}
                  <p className="text-sm text-neutral-700 whitespace-pre-line">{r.text}</p>
                  {r.photos && r.photos.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {r.photos.map((p, i) => (
                        <a key={i} href={p} target="_blank" rel="noreferrer" className="w-14 h-14 border border-neutral-200 rounded-sm overflow-hidden">
                          <img src={p} alt={`Review photo ${i + 1}`} className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {r.status !== 'approved' && (
                    <button
                      onClick={() => moderate(r.id, 'approved')}
                      disabled={busyId === r.id}
                      data-testid={`admin-review-approve-${r.id}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
                    ><Check className="w-3.5 h-3.5" /> Approve</button>
                  )}
                  {r.status !== 'rejected' && (
                    <button
                      onClick={() => moderate(r.id, 'rejected')}
                      disabled={busyId === r.id}
                      data-testid={`admin-review-reject-${r.id}`}
                      className="bg-neutral-700 hover:bg-neutral-800 text-white text-xs font-bold px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
                    ><X className="w-3.5 h-3.5" /> Reject</button>
                  )}
                  <button
                    onClick={() => del(r.id)}
                    disabled={busyId === r.id}
                    data-testid={`admin-review-delete-${r.id}`}
                    className="border border-neutral-300 hover:border-red-400 hover:text-red-600 text-xs font-bold px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
                  ><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReviewsAdmin;
